import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Cookie, X } from 'lucide-react';

const CONSENT_KEY = 'espacero_cookie_consent';

declare global {
    interface Window {
        gtag?: (...args: any[]) => void;
    }
}

type ConsentValue = 'accepted' | 'declined' | null;

export const CookieConsent: React.FC = () => {
    const { t } = useTranslation(["common", "home", "search", "listing", "profile", "add_listing", "admin"]);
    const [consent, setConsent] = useState<ConsentValue>(null);
    const [isBannerOpen, setIsBannerOpen] = useState(false);

    useEffect(() => {
        const stored = localStorage.getItem(CONSENT_KEY) as ConsentValue;
        if (!stored) {
            setIsBannerOpen(true);
        } else {
            setConsent(stored);
            if (stored === 'accepted') {
                enableAnalytics();
            }
        }
    }, []);

    const enableAnalytics = () => {
        if (window.gtag) {
            window.gtag('consent', 'update', {
                'ad_storage': 'granted',
                'analytics_storage': 'granted'
            });
        }
    };

    const handleAccept = () => {
        localStorage.setItem(CONSENT_KEY, 'accepted');
        setConsent('accepted');
        setIsBannerOpen(false);
        enableAnalytics();
    };

    const handleDecline = () => {
        localStorage.setItem(CONSENT_KEY, 'declined');
        setConsent('declined');
        setIsBannerOpen(false);
        // Explicitly deny if needed, though default should be denied/restricted if GTM is configured correctly
        if (window.gtag) {
            window.gtag('consent', 'update', {
                'ad_storage': 'denied',
                'analytics_storage': 'denied'
            });
        }
    };

    if (!isBannerOpen) {
        // Nenápadná plávajúca značka — dostupná na desktope, po kliknutí znova otvorí nastavenia súhlasu
        return (
            <button
                onClick={() => setIsBannerOpen(true)}
                aria-label={t('cookies.settings')}
                title={t('cookies.settings')}
                className="hidden md:flex fixed md:left-4 md:bottom-4 z-40 w-10 h-10 items-center justify-center rounded-full bg-stone-950/85 dark:bg-stone-100/90 text-stone-100 dark:text-stone-900 shadow-lg backdrop-blur-sm border border-stone-800 dark:border-stone-300 opacity-70 hover:opacity-100 hover:scale-110 transition-all duration-200"
            >
                <Cookie className="w-5 h-5" aria-hidden="true" />
            </button>
        );
    }

    // GDPR consent symmetry: accepting and declining must be equally easy —
    // both buttons share identical size and near-identical visual weight.
    // Compact layout so the banner does not cover a quarter of a phone screen.
    return (
        <div className="fixed bottom-0 left-0 right-0 px-3 py-2.5 md:p-4 bg-stone-950/95 text-white backdrop-blur-sm z-[100] border-t border-stone-800 shadow-2xl pb-safe">
            <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-2.5 md:gap-4">
                <div className="text-[11px] md:text-sm text-stone-200 flex-1 text-center md:text-left leading-snug">
                    <p dangerouslySetInnerHTML={{ __html: t('cookies.text') }} />
                </div>
                <div className="flex items-center gap-2 w-full md:w-auto">
                    <button
                        onClick={handleDecline}
                        className="flex-1 md:flex-none px-5 py-2.5 text-sm font-bold text-white bg-stone-800 hover:bg-stone-700 rounded-lg border border-stone-600 transition-colors"
                    >
                        {t('cookies.decline')}
                    </button>
                    <button
                        onClick={handleAccept}
                        className="flex-1 md:flex-none px-5 py-2.5 text-sm font-bold text-stone-950 bg-white hover:bg-stone-50 rounded-lg border border-white shadow-sm transition-colors"
                    >
                        {t('cookies.accept')}
                    </button>
                    {consent && (
                        <button
                            onClick={() => setIsBannerOpen(false)}
                            aria-label={t('cookies.close')}
                            className="p-2 text-stone-400 hover:text-white hover:bg-stone-800 rounded-lg transition-colors"
                        >
                            <X className="w-4 h-4" aria-hidden="true" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
